/**
 * Brand logos for filter cards (FRIDGE-1088).
 *
 * The owner, on the filter number index: "WHERE ARE LOGOS, FFN ETC AND PHOTOS
 * FIX SITEWIDE". Every filter card shows its brand's logo when the brand has
 * one, and a card with no usable photo shows a designed brand tile (the logo,
 * or the brand's name, plus the part number), never the Fridge.com wordmark.
 * That last rule is the FRIDGE-635 ruling applied to filters: "if no photo use
 * logo of brand".
 *
 * ONE lookup, keyed by brand name. `brands.name` is unique (brands_name_key),
 * and the filter rows' `products.brand` strings match it exactly: measured
 * 09/19/2026 over the live filter set, 20 brands carry a logo and all 20
 * match on exact case (a case-insensitive join finds the same 20). Keys are
 * still lower-cased on OUR side so a card never misses its logo on casing.
 *
 * FAILS OPEN. A read error returns an empty map: every card then shows the
 * brand's name and the name tile, which is the designed fallback, not a
 * broken state. A logo is decoration on a listing; it never blocks the page.
 */
import type { SupabaseClient } from '@supabase/supabase-js'
import { getProxiedStoragePath } from '@/lib/image-url'

/** Lower-cased brand name → same-origin logo path (`/brand-logos/…`). */
export type BrandLogoMap = Readonly<Record<string, string>>

/** The map key for a brand name: trimmed, single-spaced, lower-cased. */
export function brandLogoKey(brand: string): string {
  return brand.trim().replace(/\s+/g, ' ').toLowerCase()
}

/** A `logo_path` worth rendering: non-empty and not a placeholder asset. */
export function isUsableLogoPath(path: string | null | undefined): path is string {
  return typeof path === 'string' && path.trim() !== '' && !/placeholder/i.test(path)
}

/** Pure: rows from `brands (name, logo_path)` → the lookup map. */
export function buildBrandLogoMap(rows: ReadonlyArray<{ name: string | null; logo_path: string | null }>): BrandLogoMap {
  const map: Record<string, string> = {}
  for (const row of rows) {
    if (!row.name || !isUsableLogoPath(row.logo_path)) continue
    // Same-origin path: the next.config rewrite serves the bytes, so no raw
    // storage host reaches the rendered HTML or the RSC payload (FRIDGE-285).
    map[brandLogoKey(row.name)] = String(getProxiedStoragePath(row.logo_path.trim()))
  }
  return map
}

/** The logo for one brand, or null when it has none. */
export function logoFor(map: BrandLogoMap, brand: string | null | undefined): string | null {
  if (!brand) return null
  return map[brandLogoKey(brand)] ?? null
}

/**
 * Load the logos for the named brands. Bounded by the names passed in (the
 * brands a page actually renders), so the read is one indexed `IN` over the
 * unique name index and the map a client component receives holds only what
 * it shows.
 */
export async function loadBrandLogoMap(
  db: SupabaseClient,
  brands: Iterable<string | null | undefined>
): Promise<BrandLogoMap> {
  const names = [...new Set([...brands].filter((b): b is string => typeof b === 'string' && b.trim() !== '').map((b) => b.trim()))]
  if (names.length === 0) return {}
  try {
    const { data, error } = await db
      .from('brands')
      .select('name, logo_path')
      .in('name', names)
      .order('name', { ascending: true })
    if (error || !data) return {}
    return buildBrandLogoMap(data as Array<{ name: string | null; logo_path: string | null }>)
  } catch {
    return {}
  }
}
